import { WHOLESALE_RANGE, RETAIL_RANGE } from "@/constants/aiTiersCatalog";
import { getSiteBrand } from "@/lib/siteHosts";

export const AGENCY_THESIS = {
  tag: "AGENCY VALUATION // 07",
  status: "SERVICE → INFRASTRUCTURE MULTIPLE",
  headline: "Benefits for Your Agency",
  get insight() {
    return `Deploying ${getSiteBrand().name} does more than add a feature. It shifts how your agency earns, retains, and gets valued.`;
  },
};

export const VALUATION_SNAPSHOT = [
  { value: "+$3–6k", label: "MRR / mo", sub: "at 15 clients" },
  { value: "72hr", label: "Deploy", sub: "time-to-value" },
  { value: "SaaS-like", label: "Multiple", sub: "valuation lift" },
];

export const DIRECT_BENEFITS = [
  {
    id: "margin",
    code: "FI-01",
    title: "Immediate High-Margin Upsell (MRR Expansion)",
    highlight: "$140–$400+ margin / client / mo",
    description:
      `You buy an infrastructure slot at wholesale (${WHOLESALE_RANGE}/mo) and bundle it into retainers at retail (${RETAIL_RANGE}/mo). Credits pool across your roster — heavy clients draw from unused capacity elsewhere. With 15 active clients, that's $3,000–$6,000+ of pure recurring profit without new headcount.`,
  },
  {
    id: "onboard",
    code: "FI-02",
    title: "Instant Client Onboarding (72-Hour Time-to-Value)",
    highlight: "Live Digital Assistant in under 72 hours",
    description:
      "You don't need to spend weeks writing custom code or building complex automation workflows. We deploy a verified, stress-tested Digital Assistant connected to your client's CRM via API in under 72 hours. You look like an enterprise tech partner; we handle the backend.",
  },
  {
    id: "overhead",
    code: "FI-03",
    title: "Zero Infrastructure Overhead",
    highlight: "Flat, predictable cost",
    description:
      "We completely absorb the technical debt. You don't pay for separate OpenAI/Anthropic API tokens, external servers, or database maintenance. Your cost is flat, predictable, and pooled across your client roster.",
  },
  {
    id: "pool",
    code: "FI-04",
    title: "Shared Agency Credit Pool",
    highlight: "Aggregate usage, not per-client silos",
    description:
      "Every deployed client contributes tier credits to one agency pool. A client that spikes to 180 credits can borrow from nine lighter clients at 60 — you only pay for total pool usage, not wasted individual caps.",
  },
];

export const INDIRECT_BENEFITS = [
  {
    id: "churn",
    code: "ST-01",
    title: "Drastic Churn Rate Reduction (Protecting Core Retainers)",
    highlight: "2× ad ROI perception",
    description:
      "Clients drop marketing agencies because their in-house sales teams fail to call leads back on weekends, leading them to blame \"bad ad traffic.\" By plugging the lead leakage with a 9-second Digital Assistant intercept, your Google Ads/SEO ROI looks twice as efficient. Your core marketing retainers stay secure for years.",
  },
  {
    id: "ltv",
    code: "ST-02",
    title: "Client LTV Multiplier",
    highlight: "Vendor → infrastructure asset",
    description:
      "When you inject automated, vetted appointments directly into a client's CRM while they sleep, you cease to be a \"vendor who buys clicks\" and become an irreplaceable infrastructure asset. Breaking up with your agency means breaking their core operational workflow.",
  },
  {
    id: "pitch",
    code: "ST-03",
    title: "Unfair Advantage in Cold Outreach & Pitches",
    highlight: "Sell booked revenue, not impressions",
    description:
      "Stop pitching \"we do high-quality Google Ads\" like every other agency on LinkedIn. Pitch an autonomous, policy-compliant digital employee that captures after-hours market share. Your closing rate on sales calls doubles because you are selling booked revenue, not impressions.",
  },
  {
    id: "valuation",
    code: "ST-04",
    title: "Agency Valuation Lift (Moving from Service to SaaS)",
    highlight: "Recurring infra revenue",
    description:
      "Service-based agencies are valued at low multiples because they depend on human hours. By wrapping your marketing services around a white-labeled AI infrastructure engine, you build predictable SaaS-like recurring revenue, instantly driving up your company's value in the eyes of future buyers or investors.",
  },
];

export const VALUATION_MERGE = {
  label: "Combined valuation effect",
  text: "Predictable MRR today + defensible client moat tomorrow = a higher multiple when you sell or scale.",
};
